import { useTranslation } from "react-i18next";
import { useTheme } from "antd-style";
import { getMachineTypes } from "@/apis";
import { useEffect, useState } from "react";
import { notification } from "antd";
import { requiredRule } from "./useBasicComponent";
import { useResourceContext } from "@/layouts/ResourceContext";
import Mark from "mark.js";
import { useRef } from "react";
// import { machineTypeData } from "./templatedata";

export const useInstanceTypeComponent = () => {
  const { t } = useTranslation();
  const {
    rootState: { eks },
  } = useResourceContext();

  const token = useTheme();

  const styles = `
  .highlight {    
    background-color: #fafab5;
    color: ${token.colorPrimary}; 
  }  
`;

  const [machineTypeLoading, setMachineTypeLoading] = useState(false);
  const [machineTypeDisabled, setMachineTypeDisabled] = useState(false);
  const [region, setRegion] = useState("");
  const [machineTypeEnum, setMachineTypeEnum] = useState([]);

  useEffect(() => {
    const fetchContext = async () => {
      if (eks.isError) {
        setMachineTypeLoading(true);
        setMachineTypeDisabled(true);
        notification.error({
          description: t(
            "config.instance.error.context",
            "Fail to get context from server. Please refresh browser later",
          ),
        });
        return;
      }
      if (eks.isLoading) {
        setMachineTypeLoading(true);
        setMachineTypeDisabled(true);
        notification.error({
          description: t(
            "config.instance.error.contextLoading",
            "Loading context from server. Please re-launch instance later.",
          ),
        });
        return;
      }

      const { region: eksRegion } = eks.data;
      setRegion(eksRegion);
    };

    fetchContext();
  }, [JSON.stringify(eks)]);

  useEffect(() => {
    const loadMachineTypes = async () => {
      setMachineTypeLoading(true);
      setMachineTypeDisabled(true);

      if (region === "") return;
      const machineTypes = await getMachineTypes(region);
      // const machineTypes = machineTypeData;
      if (machineTypes.status !== "OK") {
        notification.error({
          description: t(
            "config.instance.error.machineTypeLoading",
            "Fail to load machine type from server. Please re-open wizard later.",
          ),
        });
        return;
      }
      const data = machineTypes.value;
      const options = Object.keys(data).map((key) => ({
        label: key,
        value: key,
        type: data[key].type,
        family: data[key].family,
        vcpu: data[key].vcpu,
        memory: data[key].memory,
        currentGeneration:
          data[key].currentGeneration === "Yes" ? "true" : "false",
        architecture: data[key].architecture,
        description: data[key].description,
      }));
      setMachineTypeEnum(options);
      setMachineTypeLoading(false);
      setMachineTypeDisabled(false);
    };

    loadMachineTypes();
  }, [region]);

  const [searchValue, setSearchValue] = useState("");
  const markRefs = useRef([]);

  const markInstanceRef = useRef(null);

  useEffect(() => {
    const s = markRefs.current.filter((item) => item != null);
    if (markInstanceRef.current) {
      markInstanceRef.current.unmark({
        done: () => {
          markInstanceRef.current = new Mark(s);
          markInstanceRef.current.mark(searchValue, { className: "highlight" });
        },
        className: "highlight",
      });
    } else {
      markInstanceRef.current = new Mark(s);
      markInstanceRef.current.mark(searchValue);
    }
  }, [searchValue]);

  const instanceTypeComponent = (group) => ({
    title: t("config.instance.form.instanceTypes", "Type"),
    dataIndex: `${group}.instanceTypes`,
    valueType: "select",

    fieldProps: {
      showSearch: true,
      loading: machineTypeLoading,
      disabled: machineTypeDisabled,
      options: machineTypeEnum,
      onSearch: (value) => {
        setSearchValue(value);
      },
      optionRender: (option, { index }) => (
        <>
          <style key={index + "_"}>{styles}</style>
          <div
            key={index}
            ref={(el) => {
              markRefs.current[index] = el;
            }}
          >
            <div>
              <strong>{option.data.label}</strong>
            </div>
            <div style={{ paddingLeft: 10, display: "flex", gap: 20 }}>
              <div>{`Family: ${option.data.family}`}</div>
              <div>{`${option.data.vcpu} vcpu`}</div>
              <div>{`${option.data.memory} Memory`}</div>
              <div>
                {`Current generation: ${option.data.currentGeneration}`}
              </div>
            </div>
            {option.data.description
              .split("\n")
              .map((item) =>
                item.indexOf("per Unused Reservation Linux") !== -1 ? (
                  <div style={{ paddingLeft: 10 }}>{item}</div>
                ) : undefined,
              )
              .filter((item) => item != undefined)}
          </div>
        </>
      ),
      filterOption: (input, option) => {
        const description = option?.description
          .split("\n")
          .map((item) =>
            item.indexOf("per Unused Reservation Linux") !== -1
              ? item
              : undefined,
          )
          .filter((item) => item != undefined)
          .join(" ");
        return (
          (option?.label ?? "").toLowerCase().includes(input.toLowerCase()) ||
          (option?.family ?? "").toLowerCase().includes(input.toLowerCase()) ||
          (option?.vcpu ?? "").toLowerCase().includes(input.toLowerCase()) ||
          (option?.memory ?? "").toLowerCase().includes(input.toLowerCase()) ||
          (option?.currentGeneration ?? "")
            .toLowerCase()
            .includes(input.toLowerCase()) ||
          (description ?? "").toLowerCase().includes(input.toLowerCase())
        );
      },
    },
    formItemProps: {
      rules: [...requiredRule(t)],
    },
    width: "500px",
    colProps: { xs: 24, sm: 8 },
  });

  return {
    instanceTypeComponent,
  };
};
